import React, { useEffect, useMemo, useState } from 'react';
import { DownloadOutlined, EditOutlined, FormOutlined, SaveOutlined, ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons';

import { Button, message, Popconfirm, theme } from 'antd';
import AnnotationFilterDropdown, { type AnnotationDropdownOption } from './AnnotationFilterDropdown';
import AnnotationToolButton from './AnnotationToolButton';
import type { Annotation as AnnotationType } from '../annotationTypes';
import {
  shouldShowAnnotationDeleteAction,
  shouldShowAnnotationToolbarSave,
} from '../../inspections/inspectionTabs';

interface AnnotationImageControlsProps {
  zoomIn: () => void;
  zoomOut: () => void;
  isAddAnnotation: boolean;
  onToggleAnnotation: () => void;
  onDownload: () => void;
  onSaveCanvas: () => Promise<boolean>;
  onDamageCodeChange: (value: string) => void;
  onSaveAnnotation: (annotation: AnnotationType) => Promise<void>;
  onDeleteAnnotation: (annotationId: string) => Promise<void>;
  onClearAnnotation: (annotationId: string) => void;
  selectedId?: string;
  annotations: AnnotationType[];
  damageSeverity?: { items?: AnnotationDropdownOption[] };
  damageTypes?: { items?: AnnotationDropdownOption[] };
  damageArea?: { items?: AnnotationDropdownOption[] };
  disableAnnotation?: boolean;
  activeTab?: string;
}

const AnnotationImageControls: React.FC<AnnotationImageControlsProps> = ({
  zoomIn,
  zoomOut,
  isAddAnnotation,
  onToggleAnnotation,
  onDownload,
  onSaveCanvas,
  onDamageCodeChange,
  onSaveAnnotation,
  onDeleteAnnotation,
  onClearAnnotation,
  selectedId,
  annotations,
  damageSeverity,
  damageTypes,
  damageArea,
  disableAnnotation = false,
  activeTab,
}) => {
  const { token } = theme.useToken();
  const [activeToolId, setActiveToolId] = useState<string | null>(null);
  const [damageType, setDamageType] = useState<string>('00');
  const [partType, setPartType] = useState<string>('00');
  const [severityType, setSeverityType] = useState<string>('0');
  const [showSaveAction, setShowSaveAction] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isToolbarSaving, setIsToolbarSaving] = useState(false);
  const [isCompactViewport, setIsCompactViewport] = useState(false);

  const isDisabled = damageType === '00' || partType === '00' || severityType === '0' || isSaving || isDeleting;

  const selectedAnnotation = useMemo(() => annotations.find(annotation => annotation.id === selectedId), [annotations, selectedId]);
  const isSavedDamage = Boolean(selectedAnnotation?.id) && !String(selectedAnnotation?.id ?? '').includes('new_');
  const hasSavedDamage = useMemo(
    () => annotations.some(annotation => Boolean(annotation?.id) && !String(annotation?.id ?? '').includes('new_')),
    [annotations]
  );
  const showDeleteAction = shouldShowAnnotationDeleteAction(activeTab, isSavedDamage);
  const showToolbarSave = shouldShowAnnotationToolbarSave(activeTab, hasSavedDamage);
  const hasPendingCanvasChanges = useMemo(
    () =>
      annotations.some(
        annotation =>
          String(annotation?.id ?? '').includes('new_') &&
          annotation?.deleted !== 1 &&
          String(annotation?.type ?? '').substring(0, 2) !== '00' &&
          String(annotation?.type ?? '').substring(2, 4) !== '00' &&
          String(annotation?.type ?? '').substring(4, 5) !== '0'
      ),
    [annotations]
  );

  useEffect(() => {
    const updateViewportMode = () => {
      const nextIsCompactViewport = window.innerWidth < 1024 || (window.innerWidth <= 1366 && window.innerHeight <= 650);
      setIsCompactViewport(nextIsCompactViewport);
    };

    updateViewportMode();
    window.addEventListener('resize', updateViewportMode);

    return () => {
      window.removeEventListener('resize', updateViewportMode);
    };
  }, []);

  const applyDamageCodeChange = (part: string, damage: string, severity: string) => {
    onDamageCodeChange(part + damage + severity);
  };

  useEffect(() => {
    if (selectedAnnotation) {
      setPartType(selectedAnnotation.type.substring(0, 2));
      setDamageType(selectedAnnotation.type.substring(2, 4));
      setSeverityType(selectedAnnotation.type.substring(4, 5));
      setShowSaveAction(Boolean(selectedAnnotation.action) || Boolean(selectedAnnotation.id));
    } else {
      setPartType('00');
      setDamageType('00');
      setSeverityType('0');
      setShowSaveAction(false);
    }
  }, [selectedAnnotation]);

  const handlePartTypeChange = (value: string) => {
    setPartType(value);
    applyDamageCodeChange(value, damageType, severityType);
  };

  const handleDamageTypeChange = (value: string) => {
    setDamageType(value);
    applyDamageCodeChange(partType, value, severityType);
  };

  const handleSeverityTypeChange = (value: string) => {
    setSeverityType(value);
    applyDamageCodeChange(partType, damageType, value);
  };

  const removeAnnotation = async () => {
    if (isDeleting) return;
    if (!selectedId) {
      message.warning('No annotation selected to delete.');
      return;
    }

    try {
      setIsDeleting(true);
      await onDeleteAnnotation(selectedId);
      message.success('Annotation deleted successfully.');
    } catch (error) {
      message.error('Failed to delete annotation. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const clearAnnotation = () => {
    if (!selectedId) {
      message.warning('No annotation selected to clear.');
      return;
    }

    onClearAnnotation(selectedId);
  };

  const setSave = async () => {
    if (isSaving) return;

    setIsSaving(true);

    if (!selectedAnnotation) {
      message.warning('No annotation selected to save.');
      setIsSaving(false);
      return;
    }

    try {
      await onSaveAnnotation(selectedAnnotation);
      message.success('Annotation saved successfully.');
      setShowSaveAction(false);
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Failed to save annotation. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToolbarSave = async () => {
    if (isToolbarSaving) return;

    try {
      setIsToolbarSaving(true);
      const didSave = await onSaveCanvas();

      if (didSave) {
        message.success('Annotation saved successfully.');
      } else {
        message.warning('No new annotation available to save.');
      }
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Failed to save annotation. Please try again.');
    } finally {
      setIsToolbarSaving(false);
    }
  };

  const getToolIconStyle = (isToolActive: boolean) => ({
    color: isToolActive ? token.colorPrimary : '#000000',
    transition: 'color 0.2s ease',
  });
  const actionButtonClassName = isCompactViewport
    ? '!h-[26px] !min-w-[46px] !px-1.5 !text-[10px]'
    : '!h-[30px] !min-w-[52px] !px-2 !text-[11px]';

  const tools = [
    {
      id: 'zoomIn',
      label: 'Zoom In',
      isActive: activeToolId === 'zoomIn',
      icon: <ZoomInOutlined className="h-4 w-4" style={getToolIconStyle(activeToolId === 'zoomIn')} />,
      onClick: zoomIn,
    },
    {
      id: 'zoomOut',
      label: 'Zoom Out',
      isActive: activeToolId === 'zoomOut',
      icon: <ZoomOutOutlined className="h-4 w-4" style={getToolIconStyle(activeToolId === 'zoomOut')} />,
      onClick: zoomOut,
    },
    {
      id: 'annotation',
      label: isAddAnnotation ? 'Disable Annotation' : 'Enable Annotate',
      isActive: activeToolId === 'annotation' || isAddAnnotation,
      icon: isAddAnnotation ? (
        <FormOutlined className="h-4 w-4" style={getToolIconStyle(activeToolId === 'annotation' || isAddAnnotation)} />
      ) : (
        <EditOutlined className="h-4 w-4" style={getToolIconStyle(activeToolId === 'annotation' || isAddAnnotation)} />
      ),
      onClick: onToggleAnnotation,
      disabled: disableAnnotation,
    },
    ...(showToolbarSave
      ? [
          {
            id: 'save',
            label: 'Save',
            isActive: activeToolId === 'save',
            icon: <SaveOutlined className="h-4 w-4" style={getToolIconStyle(activeToolId === 'save')} />,
            onClick: handleToolbarSave,
            disabled: disableAnnotation || !hasPendingCanvasChanges || isToolbarSaving,
          },
        ]
      : []),
    {
      id: 'download',
      label: 'Download original Image',
      isActive: activeToolId === 'download',
      icon: <DownloadOutlined className="h-4 w-4" style={getToolIconStyle(activeToolId === 'download')} />,
      onClick: onDownload,
    },
  ];

  return (
    <>
      {selectedAnnotation && !disableAnnotation && isAddAnnotation && (
        <div
        className="absolute left-1/2 top-3 z-20 w-[calc(100vw-24px)] max-w-fit -translate-x-1/2 rounded-[5px] border border-white/90 bg-black/15 backdrop-blur-sm"
      >
        <div
          className={`
            popup-operation
            flex
            flex-col
            items-center
            gap-2
            rounded-lg
            p-2
            lg:flex-row
            lg:gap-1
          `}
        >
          {/* Dropdowns */}
          <div className="flex w-full flex-wrap items-center justify-center gap-1 lg:w-auto lg:flex-nowrap">
            <AnnotationFilterDropdown
              value={partType}
              placeholder="Select Parts"
              options={damageArea?.items}
              defaultValue="00"
              onChange={handlePartTypeChange}
              compact={isCompactViewport}
            />
      
            <AnnotationFilterDropdown
              value={damageType}
              placeholder="Damages"
              options={damageTypes?.items}
              defaultValue="00"
              onChange={handleDamageTypeChange}
              compact={isCompactViewport}
            />
      
            <AnnotationFilterDropdown
              value={severityType}
              placeholder="Severities"
              options={damageSeverity?.items}
              defaultValue="0"
              onChange={handleSeverityTypeChange}
              compact={isCompactViewport}
            />
          </div>
      
          {/* Actions */}
          <div className="flex w-full items-center justify-center gap-1 lg:w-auto">
            <span className="hidden text-sm font-light text-white lg:inline">
              |
            </span>
      
            {showSaveAction && (!showToolbarSave || isSavedDamage) && (
              <Button
                onClick={setSave}
                disabled={isDisabled}
                loading={isSaving}
                className={`${actionButtonClassName} !rounded-[7px] !border-0 !bg-[#555] !font-semibold !text-white !shadow-none`}
              >
                Save
              </Button>
            )}
      
            {showDeleteAction ? (
              <Popconfirm
                title="Delete annotation?"
                okText="Delete"
                cancelText="Cancel"
                onConfirm={removeAnnotation}
                okButtonProps={{
                  loading: isDeleting,
                  disabled: isDeleting,
                }}
                cancelButtonProps={{
                  disabled: isDeleting,
                }}
              >
                <Button
                  loading={isDeleting}
                  disabled={isDeleting}
                  className={`${actionButtonClassName} !rounded-[7px] !border !border-slate-200 !bg-slate-50 !font-semibold !text-slate-600`}
                >
                  Delete
                </Button>
              </Popconfirm>
            ) : (
              <Button
                onClick={clearAnnotation}
                className={`${actionButtonClassName} !rounded-[7px] !border !border-slate-200 !bg-slate-50 !font-semibold !text-slate-600`}
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </div>
      )}

      <div className="absolute bottom-[15px] left-3 right-3 z-10 flex flex-row flex-wrap gap-3 sm:left-5 sm:right-auto sm:gap-5">
        {tools.map(({ id, label, icon, onClick, disabled, isActive }) => (
          <AnnotationToolButton
            key={id}
            label={label}
            icon={icon}
            isActive={Boolean(isActive)}
            disabled={disabled}
            onClick={() => {
              if (disabled) return;
              setActiveToolId(id);
              onClick();
              setTimeout(() => setActiveToolId(null), 300);
            }}
          />
        ))}
      </div>
    </>
  );
};

export default AnnotationImageControls;
